import axios, { AxiosInstance, AxiosError } from 'axios'
import { CookieUtils, KIOSK_COOKIES } from './cookies'
import {
  STEP_UP_EVENT,
  handleSharedSessionUnauthorized,
  isSharedSessionActive,
} from './sharedDevice'
import {
  User,
  PublicUser,
  Assignment,
  Chore,
  LoginResponse,
  Notification,
  Achievement,
  UserAchievement,
  Reward,
  RewardPurchase,
  PointTransaction,
  LeaderboardEntry,
  Bid,
  Trade,
  MarketplaceOffer,
  OpenChoreWithBids,
  MyOpenChoreBid
} from '@/types'

// Only log in development mode
const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV
const devLog = (...args: any[]) => {
  if (isDev) console.log(...args)
}
const devWarn = (...args: any[]) => {
  if (isDev) console.warn(...args)
}
const devError = (...args: any[]) => {
  if (isDev) console.error(...args)
}

// Determine the API base URL based on the current environment
const getApiBaseUrl = () => {
  // Check for environment variable first
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  const hostname = window.location.hostname
  
  // For all .housepoints.ai domains, always use HTTPS
  if (hostname.endsWith('.housepoints.ai')) {
    const url = `https://${hostname}`
    devLog('[API] Using HTTPS for .housepoints.ai domain:', url)
    return url
  }
  
  // For localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return window.location.origin
  }
  
  // Default fallback - should not be reached in production
  devWarn('[API] Using fallback URL for hostname:', hostname)
  return `https://${hostname}`
}

// Export the function for use by other modules
export { getApiBaseUrl }

// Device token a paired kiosk display stored at activation (KioskActivatePage
// keeps it in both localStorage and a cookie).
const getKioskDeviceToken = (): string | null => {
  try {
    return localStorage.getItem(KIOSK_COOKIES.DEVICE_TOKEN) ||
      CookieUtils.getCookie(KIOSK_COOKIES.DEVICE_TOKEN)
  } catch {
    return null
  }
}

// API URL is now determined dynamically for each request
// This ensures HTTPS is used when the page is served over HTTPS

class ApiClient {
  private client: AxiosInstance

  constructor() {
    // For .housepoints.ai domains, ensure no base URL is ever set
    const axiosConfig: any = {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send cookies with requests for kiosk authentication
    }
    
    // Explicitly prevent any baseURL for .housepoints.ai domains
    if (window.location.hostname.endsWith('.housepoints.ai')) {
      axiosConfig.baseURL = null
      delete axios.defaults.baseURL
    } else {
      axiosConfig.baseURL = undefined
    }
    
    this.client = axios.create(axiosConfig)

    // Add auth token to requests (except for kiosk endpoints)
    this.client.interceptors.request.use((config) => {
      // ABSOLUTE HTTPS ENFORCEMENT for .housepoints.ai domains
      if (window.location.hostname.endsWith('.housepoints.ai')) {
        // Build the full HTTPS URL no matter what
        const httpsBase = `https://${window.location.hostname}`
        
        // CRITICAL: Set method and ensure no axios defaults interfere
        config.method = config.method || 'get'
        
        // Clear ALL base-related configs
        config.baseURL = ''
        delete config.baseURL
        delete (config as any).base
        delete (config as any).uri
        
        // Handle all possible URL formats
        if (!config.url) {
          config.url = httpsBase
        } else if (config.url.startsWith('https://')) {
          // Already HTTPS, leave it
        } else if (config.url.startsWith('http://')) {
          // Force HTTP to HTTPS
          devError('[API] BLOCKING HTTP URL:', config.url)
          config.url = config.url.replace('http://', 'https://')
        } else if (config.url.startsWith('//')) {
          // Protocol-relative URL - force HTTPS
          config.url = 'https:' + config.url
        } else if (config.url.startsWith('/')) {
          // Absolute path - prepend HTTPS base
          config.url = httpsBase + config.url
        } else {
          // Relative path - prepend HTTPS base with slash
          config.url = httpsBase + '/' + config.url
        }
        
        // Remove any trailing slashes that might cause issues
        if (config.url.endsWith('/') && config.url.length > httpsBase.length + 1) {
          config.url = config.url.slice(0, -1)
        }
        
      } else {
        // For non-.housepoints.ai domains, use the normal logic
        const apiBase = getApiBaseUrl()

        if (config.url && !config.url.startsWith('http')) {
          const separator = config.url.startsWith('/') ? '' : '/'
          config.url = apiBase + separator + config.url
          config.baseURL = undefined
        } else {
          config.baseURL = apiBase
        }
      }
      
      // Skip authentication for the Home Assistant webhook paths and
      // oauth-login. oauth-login sets its own Authorization header with the
      // platform token.
      //
      // Match on the URL path only (stripped of any host prefix) and require
      // an exact prefix. The previous `includes('/kiosk/')` was too loose and
      // matched `/api/admin/kiosk/*` (Settings → Family Kiosks management,
      // which does need Bearer auth), stripping the token and producing a 401.
      const urlPath = config.url?.startsWith('http')
        ? new URL(config.url).pathname
        : (config.url ?? '')
      const isKioskApi = urlPath.startsWith('/api/kiosk/')
      const isKioskEndpoint = isKioskApi ||
                              urlPath.startsWith('/api/ha-webhook/') ||
                              urlPath.startsWith('/api/home-assistant/')

      const isOAuthLoginEndpoint = urlPath.startsWith('/api/auth/oauth-login')

      const token = localStorage.getItem('auth_token')

      if (isKioskApi) {
        // /api/kiosk/* requires a credential for this family: the signed-in
        // user's JWT, or on a paired kiosk display the device token saved at
        // activation. The pairing bootstrap routes ignore it.
        const kioskToken = token || getKioskDeviceToken()
        if (kioskToken) {
          config.headers.Authorization = `Bearer ${kioskToken}`
        }
      } else if (!isKioskEndpoint && !isOAuthLoginEndpoint) {
        if (token) {
          // Token is now stored as plain string
          config.headers.Authorization = `Bearer ${token}`
        }
      }

      if (isKioskEndpoint) {
        // Add X-Family-Name header for kiosk endpoints
        if (window.location.hostname.endsWith('.housepoints.ai')) {
          const familyName = window.location.hostname.split('.')[0]
          config.headers['X-Family-Name'] = familyName
          devLog('[API] Adding X-Family-Name header for kiosk endpoint:', familyName)
        }
      }
      // Note: oauth-login endpoint sets Authorization header explicitly with platform token
      
      return config
    })

    // Token refresh logic
    let isRefreshing = false
    let refreshSubscribers: ((token: string) => void)[] = []

    const subscribeTokenRefresh = (callback: (token: string) => void) => {
      refreshSubscribers.push(callback)
    }

    const onTokenRefreshed = (token: string) => {
      refreshSubscribers.forEach(callback => callback(token))
      refreshSubscribers = []
    }

    const refreshAuthToken = async (): Promise<string | null> => {
      try {
        const currentToken = localStorage.getItem('auth_token')
        if (!currentToken) return null

        const response = await axios.post('/api/auth/refresh', null, {
          headers: { Authorization: `Bearer ${currentToken}` }
        })

        const newToken = response.data.access_token
        localStorage.setItem('auth_token', newToken)

        // Log token refresh for monitoring
        devLog('[Auth] Token refreshed successfully')

        return newToken
      } catch (error) {
        // Keep console.error for production debugging of auth failures
        console.error('[Auth] Token refresh failed:', error)
        return null
      }
    }

    // Handle auth errors with retry and refresh
    this.client.interceptors.response.use(
      (response) => {
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any

        // Shared family device sessions: never refresh or send the screen to
        // /login. A 401 means the member session (or the device) is no longer
        // valid; a 403 marked step_up_required asks for a parent's approval.
        if (isSharedSessionActive()) {
          const data = (error.response?.data || {}) as any
          if (error.response?.status === 401) {
            handleSharedSessionUnauthorized(data.reason)
            return Promise.reject(error)
          }
          if (error.response?.status === 403 && (data.step_up_required || data.error === 'step_up_required')) {
            const urlPath = originalRequest?.url?.startsWith('http')
              ? new URL(originalRequest.url).pathname
              : (originalRequest?.url ?? '')
            window.dispatchEvent(new CustomEvent(STEP_UP_EVENT, {
              detail: { method: (originalRequest?.method || 'get').toUpperCase(), path: urlPath },
            }))
            return Promise.reject(error)
          }
        }

        if (error.response?.status === 401) {
          // Skip auth handling for kiosk endpoints, oauth-login, and non-critical endpoints
          // that can fail during OAuth race conditions
          const isKioskEndpoint = originalRequest?.url?.includes('/api/kiosk/') ||
                                  originalRequest?.url?.includes('/kiosk/') ||
                                  originalRequest?.url?.includes('/api/ha-webhook/') ||
                                  originalRequest?.url?.includes('/api/home-assistant/') ||
                                  originalRequest?.url?.includes('/api/auth/refresh') ||
                                  originalRequest?.url?.includes('/api/auth/oauth-login')

          // Non-critical endpoints that shouldn't trigger logout on 401
          // These can fail during OAuth token synchronization race conditions
          const isNonCriticalEndpoint = originalRequest?.url?.includes('/api/setup/status') ||
                                        originalRequest?.url?.includes('/api/subscription/status')

          // Also skip redirect if we're currently on a kiosk page
          const isKioskPage = window.location.pathname.startsWith('/kiosk')

          if (isKioskEndpoint || isKioskPage || isNonCriticalEndpoint) {
            return Promise.reject(error)
          }
          
          const token = localStorage.getItem('auth_token')
          const user = localStorage.getItem('user')
          
          // Don't retry if we've already tried
          if (originalRequest._retry) {
            devLog('[Auth] Already retried, logging out user')

            // Clear auth data
            localStorage.removeItem('auth_token')
            localStorage.removeItem('user')
            localStorage.removeItem('google_auth_status')
            sessionStorage.setItem('redirect_after_login', window.location.pathname)
            
            // Add delay before redirect to prevent rapid redirects
            setTimeout(() => {
              window.location.href = '/login'
            }, 1000)
            
            return Promise.reject(error)
          }
          
          originalRequest._retry = true
          
          // Try to refresh token if we have one
          if (token && user) {
            if (!isRefreshing) {
              isRefreshing = true
              
              const newToken = await refreshAuthToken()
              isRefreshing = false
              
              if (newToken) {
                onTokenRefreshed(newToken)
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return this.client(originalRequest)
              } else {
                // Refresh failed, check if OAuth error
                const errorData = error.response?.data as any
                const isOAuthError = errorData?.detail?.includes?.('Google') ||
                                    errorData?.detail?.includes?.('OAuth')

                if (isOAuthError) {
                  devLog('[Auth] OAuth token expired, redirecting to re-authenticate...')
                  sessionStorage.setItem('oauth_return_url', window.location.pathname)
                }
                
                // Clear auth and redirect
                localStorage.removeItem('auth_token')
                localStorage.removeItem('user')
                localStorage.removeItem('google_auth_status')
                sessionStorage.setItem('redirect_after_login', window.location.pathname)
                
                setTimeout(() => {
                  window.location.href = '/login'
                }, 1000)
              }
            } else {
              // Wait for token refresh to complete
              return new Promise((resolve) => {
                subscribeTokenRefresh((newToken: string) => {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`
                  resolve(this.client(originalRequest))
                })
              })
            }
          } else {
            // No authentication at all - redirect to login
            devLog('[Auth] No token found, redirecting to login')
            sessionStorage.setItem('redirect_after_login', window.location.pathname)
            
            setTimeout(() => {
              window.location.href = '/login'
            }, 1000)
          }
        }
        
        return Promise.reject(error)
      }
    )
  }

  // Generic HTTP methods for external use
  async get(url: string, config?: any) {
    // Extra safety check for .housepoints.ai domains
    if (window.location.hostname.endsWith('.housepoints.ai')) {
      // ALWAYS use HTTPS for these domains, no exceptions
      const httpsUrl = url.startsWith('/') ? `https://${window.location.hostname}${url}` : url
      devLog(`[API] GET request to: ${httpsUrl}`)
      const response = await this.client.get(httpsUrl, config)
      return response.data
    }
    const response = await this.client.get(url, config)
    return response.data
  }

  async post(url: string, data?: any, config?: any) {
    // Extra safety check for .housepoints.ai domains
    if (window.location.hostname.endsWith('.housepoints.ai')) {
      // ALWAYS use HTTPS for these domains, no exceptions
      const httpsUrl = url.startsWith('/') ? `https://${window.location.hostname}${url}` : url
      devLog(`[API] POST request to: ${httpsUrl}`)
      const response = await this.client.post(httpsUrl, data, config)
      return response.data
    }
    const response = await this.client.post(url, data, config)
    return response.data
  }

  async put(url: string, data?: any) {
    // Extra safety check for .housepoints.ai domains
    if (window.location.hostname.endsWith('.housepoints.ai')) {
      const httpsUrl = url.startsWith('/') ? `https://${window.location.hostname}${url}` : url
      devLog(`[API] PUT request to: ${httpsUrl}`)
      const response = await this.client.put(httpsUrl, data)
      return response.data
    }
    const response = await this.client.put(url, data)
    return response.data
  }

  async patch(url: string, data?: any) {
    // Extra safety check for .housepoints.ai domains
    if (window.location.hostname.endsWith('.housepoints.ai')) {
      const httpsUrl = url.startsWith('/') ? `https://${window.location.hostname}${url}` : url
      devLog(`[API] PATCH request to: ${httpsUrl}`)
      const response = await this.client.patch(httpsUrl, data)
      return response.data
    }
    const response = await this.client.patch(url, data)
    return response.data
  }

  async delete(url: string) {
    // Extra safety check for .housepoints.ai domains
    if (window.location.hostname.endsWith('.housepoints.ai')) {
      const httpsUrl = url.startsWith('/') ? `https://${window.location.hostname}${url}` : url
      devLog(`[API] DELETE request to: ${httpsUrl}`)
      const response = await this.client.delete(httpsUrl)
      return response.data
    }
    const response = await this.client.delete(url)
    return response.data
  }

  // Auth endpoints
  // Kid Password Authentication
  async getKidPasswordAuthStatus(): Promise<{ enabled: boolean }> {
    const response = await this.client.get('/api/family/kid-password-auth')
    return response.data
  }

  async updateKidPasswordAuth(enabled: boolean): Promise<{ message: string; enabled: boolean }> {
    const response = await this.client.put('/api/family/kid-password-auth', { enabled })
    return response.data
  }

  async getUserAuthStatus(userId: string): Promise<{
    user_id: string
    username: string
    is_parent: boolean
    password_enabled: boolean
    has_oauth: boolean
  }> {
    const response = await this.client.get(`/api/users/${userId}/auth-status`)
    return response.data
  }

  async setKidPassword(userId: string, password: string): Promise<{ message: string; user_id: string; username: string }> {
    const response = await this.client.put(`/api/users/${userId}/password`, { password })
    return response.data
  }

  async disableKidPassword(userId: string): Promise<{ message: string; user_id: string; username: string }> {
    const response = await this.client.delete(`/api/users/${userId}/password`)
    return response.data
  }

  // Google OAuth Integration
  async initGoogleAuth(params: {
    scopes: string[]
    redirect_uri: string
  }): Promise<{ authorization_url: string }> {
    const response = await this.client.post('/api/auth/google/init', params)
    return response.data
  }

  async getUserGoogleAuth(params?: string): Promise<{
    isGoogleLinked: boolean
    googleEmail?: string
    calendarAccess: boolean
    lastSync?: string
    syncedCalendars?: Array<{
      id: string
      name: string
      syncDirection: string
      isPrivate: boolean
    }>
  }> {
    const response = await this.client.get(`/api/auth/google/status${params || ''}`)
    return response.data
  }

  async syncGoogleCalendar(): Promise<{ events_imported: number }> {
    const response = await this.client.post('/api/auth/google/sync-calendar')
    return response.data
  }

  async unlinkGoogleAuth(): Promise<{ message: string }> {
    const response = await this.client.delete('/api/auth/google/unlink')
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/api/auth/me')
    return response.data
  }

  async oauthLogin(platformToken: string): Promise<LoginResponse> {
    const response = await this.client.post('/api/auth/oauth-login', {}, {
      headers: {
        'Authorization': `Bearer ${platformToken}`
      }
    })
    return response.data
  }

  async refreshToken(): Promise<{ access_token: string; token_type: string }> {
    const response = await this.client.post('/api/auth/refresh')
    return response.data
  }

  // User endpoints
  async getUsers(): Promise<User[]> {
    const response = await this.client.get('/api/users')
    // Go backend returns { users: [...], count, family_id, family_name }
    return response.data.users || response.data
  }

  async getPublicUsers(): Promise<PublicUser[]> {
    const response = await this.client.get('/api/users/public')
    return response.data
  }

  async getUser(userId: string): Promise<User> {
    const response = await this.client.get(`/api/users/${userId}`)
    return response.data
  }

  async getUserByUsername(username: string): Promise<User> {
    const response = await this.client.get(`/api/users/username/${username}`)
    return response.data
  }

  async getLeaderboard(period?: 'day' | 'week' | 'month' | 'year' | 'all', offset?: number): Promise<LeaderboardEntry[]> {
    // Map period names to backend expected values
    const periodMap: { [key: string]: string } = {
      day: 'today',
      week: 'week',
      month: 'month',
      year: 'year',
      all: 'all'
    }
    const timeframe = period ? periodMap[period] : 'week'
    let url = `/api/leaderboard?timeframe=${timeframe}`
    if (offset !== undefined && offset !== 0) {
      url += `&offset=${offset}`
    }
    const response = await this.client.get(url)
    // Go backend returns { period, leaderboard: [...] }
    return response.data.leaderboard || response.data
  }

  async getUserStats(userId: string): Promise<any> {
    const response = await this.client.get(`/api/users/${userId}/stats`)
    return response.data
  }

  async getPointsHistory(userId: string, limit = 50): Promise<{
    user_id: string
    current_points: number
    transactions: PointTransaction[]
  }> {
    const response = await this.client.get(`/api/users/${userId}/points-history?limit=${limit}`)
    return response.data
  }

  async updateUserActivity(userId: string): Promise<void> {
    await this.client.post(`/api/users/${userId}/update-activity`)
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/users/${userId}`)
    return response.data
  }

  // Create a kid (non-login child) from the setup wizard. Generates a unique
  // username from the display name since kids don't sign in directly.
  async createChild(displayName: string, options?: { emoji?: string; color_theme?: string }): Promise<any> {
    const base = displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '') || 'kid'
    const username = `${base}${Math.floor(1000 + Math.random() * 9000)}`
    return this.post('/api/users', {
      username,
      display_name: displayName.trim(),
      is_parent: false,
      role: 'child',
      login_enabled: false,
      color_theme: options?.color_theme || '#3498db',
      emoji: options?.emoji || null
    })
  }

  // Chore endpoints
  async getChores(category?: string, activeOnly = true): Promise<Chore[]> {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    if (activeOnly) params.append('active_only', 'true')
    
    const response = await this.client.get(`/api/chores?${params}`)
    return response.data
  }

  async getChore(choreId: string): Promise<Chore> {
    const response = await this.client.get(`/api/chores/${choreId}`)
    return response.data
  }

  async getAvailableChores(): Promise<Assignment[]> {
    const response = await this.client.get('/api/chores/available')
    return response.data
  }

  async getChoreCategories(): Promise<{ category: string; count: number }[]> {
    const response = await this.client.get('/api/chores/categories')
    return response.data
  }

  async getChoreTags(): Promise<string[]> {
    const response = await this.client.get('/api/chores/tags')
    return response.data
  }

  // Rotation management
  async getActiveRotations(): Promise<any[]> {
    const response = await this.client.get('/api/rotations/active')
    return response.data
  }

  async getUpcomingAssignments(daysAhead = 7): Promise<any[]> {
    const response = await this.client.get(`/api/rotations/upcoming?days_ahead=${daysAhead}`)
    return response.data
  }

  async createRotation(rotationData: {
    chore_id: string
    name: string
    frequency: string
    rotation_order: string[]
    start_date: string
    skip_when_absent: boolean
    fallback_to_bidding_days: number
  }): Promise<any> {
    const response = await this.client.post('/api/rotations/create', rotationData)
    return response.data
  }

  async declarePresence(data: {
    date: string
    is_present: boolean
    reason?: string
  }): Promise<any> {
    const response = await this.client.post('/api/presence/declare', data)
    return response.data
  }

  // Parent chore management
  async getParentChores(category?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (category) params.append('category', category)

    const response = await this.client.get(`/api/chores?${params}`)
    // API returns { family_id, family_name, chores: [...], count }
    // Extract chores array for consistent return type
    return response.data.chores || response.data
  }

  async getParentChildren(dateFilter?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (dateFilter) params.append('date_filter', dateFilter)

    const response = await this.client.get(`/api/parent/children?${params}`)
    // API returns { family_id, family_name, children: [...], count }
    // Extract children array for consistent return type
    return response.data.children || response.data
  }

  async assignChores(assignments: any[]): Promise<any> {
    const response = await this.client.post('/api/parent/assign', {
      assignments,
      assignment_type: 'parent_assigned'
    })
    return response.data
  }

  async createOpenChore(data: {
    chore_id: string
    points_offered: number
    expires_at: string
    notify_present_only: boolean
  }): Promise<any> {
    // Create an open assignment for an existing chore
    const response = await this.client.post('/api/assignments', {
      chore_id: data.chore_id,
      status: 'open',
      points_offered: data.points_offered,
      due_date: data.expires_at,
    })
    return response.data
  }

  async createNewChore(choreData: {
    name: string
    description: string
    instructions: string
    category: string
    base_points: number
    estimated_minutes: number
    difficulty: string
    frequency: string
    requires_verification: boolean
    requires_photo: boolean
    tags: string[]
    default_due_time?: string
  }): Promise<any> {
    const response = await this.client.post('/api/chores', choreData)
    return response.data
  }

  async createChore(choreData: {
    name: string
    description: string
    instructions: string
    category: string
    base_points: number
    estimated_minutes: number
    difficulty: string
    requires_verification: boolean
    requires_photo: boolean
    frequency: string
    tags: string[]
    icon: string
  }): Promise<any> {
    const response = await this.client.post('/api/chores', choreData)
    return response.data
  }

  async updateChore(choreId: string, choreData: {
    name?: string
    description?: string
    instructions?: string
    category?: string
    base_points?: number
    estimated_minutes?: number
    difficulty?: string
    requires_verification?: boolean
    requires_photo?: boolean
    frequency?: string
    tags?: string[]
    icon?: string
    default_due_time?: string
    steps?: { position: number; text: string }[]
  }): Promise<any> {
    const response = await this.client.put(`/api/chores/${choreId}`, choreData)
    return response.data
  }

  async deleteChore(choreId: string): Promise<any> {
    const response = await this.client.delete(`/api/chores/${choreId}`)
    return response.data
  }

  async createAssignment(assignmentData: {
    chore_id: string
    assigned_to: string
    points_offered: number
    due_date: string
  }): Promise<any> {
    const response = await this.client.post('/api/assignments/', assignmentData)
    return response.data
  }

  // Assignment endpoints
  async getAssignments(status?: string, assignedTo?: string, dateFilter = 'today', limit = 50): Promise<Assignment[]> {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    if (assignedTo) params.append('assigned_to', assignedTo)
    params.append('date_filter', dateFilter)
    params.append('limit', limit.toString())

    const response = await this.client.get(`/api/assignments?${params}`)
    // Go backend returns { assignments: [...], count }
    return response.data.assignments || response.data
  }

  async getAssignmentHistory(startDate?: string, endDate?: string, assignedTo?: string, status?: string, limit = 1000): Promise<Assignment[]> {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (assignedTo) params.append('assigned_to', assignedTo)
    if (status) params.append('status', status)
    params.append('limit', limit.toString())

    const response = await this.client.get(`/api/assignments/history?${params}`)
    // Go backend returns { assignments: [...], count }
    return response.data.assignments || response.data
  }

  async getMyAssignments(status?: string): Promise<Assignment[]> {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    
    const response = await this.client.get(`/api/assignments/my-assignments?${params}`)
    // Go backend returns { assignments: [...], count }
    return response.data.assignments || response.data
  }

  async getAvailableAssignments(category?: string): Promise<Assignment[]> {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    
    const response = await this.client.get(`/api/assignments/available?${params}`)
    return response.data
  }

  async claimAssignment(assignmentId: string): Promise<Assignment> {
    const response = await this.client.post(`/api/assignments/${assignmentId}/claim`)
    return response.data
  }

  async startAssignment(assignmentId: string): Promise<{ message: string; started_at: string }> {
    // Start assignment by setting status to in_progress
    const response = await this.client.put(`/api/assignments/${assignmentId}`, { status: 'in_progress' })
    return { message: 'Assignment started', started_at: new Date().toISOString() }
  }

  async updateAssignment(
    assignmentId: string,
    updates: { status?: string; notes?: string }
  ): Promise<any> {
    const response = await this.client.put(`/api/assignments/${assignmentId}`, updates)
    return response.data
  }

  async completeAssignment(
    assignmentId: string, 
    completionNotes?: string
  ): Promise<{
    message: string
    completed_at: string
    requires_verification: boolean
    points_earned: number
  }> {
    const response = await this.client.post(`/api/assignments/${assignmentId}/complete`, {
      completion_notes: completionNotes
    })
    return response.data
  }

  async uploadCompletionPhoto(
    assignmentId: string,
    file: File
  ): Promise<{ message: string; photo_url: string }> {
    const formData = new FormData()
    formData.append('photo', file)
    
    const response = await this.client.post(
      `/api/assignments/${assignmentId}/upload-photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  async verifyAssignment(
    assignmentId: string,
    verificationData: {
      approved: boolean
      verification_notes?: string
      points_awarded?: number
    }
  ): Promise<{
    message: string
    verified_at: string
    approved: boolean
    points_earned: number
  }> {
    const response = await this.client.post(`/api/assignments/${assignmentId}/verify`, verificationData)
    return response.data
  }

  // Bidding endpoints
  async getBids(): Promise<Bid[]> {
    const response = await this.client.get('/api/bids/')
    return response.data
  }

  async getMyBids(): Promise<Bid[]> {
    const response = await this.client.get('/api/bids/my-bids')
    return response.data
  }

  async getBidableAssignments(): Promise<Assignment[]> {
    const response = await this.client.get('/api/assignments/bidable')
    return response.data
  }

  async createBid(bidData: {
    assignment_id: string
    points_offered: number
    message?: string
  }): Promise<Bid> {
    const response = await this.client.post('/api/bids/', bidData)
    return response.data
  }

  async acceptBid(bidId: string): Promise<{ message: string; points_earned: number }> {
    const response = await this.client.post(`/api/bids/${bidId}/accept`)
    return response.data
  }

  async rejectBid(bidId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/bids/${bidId}/reject`)
    return response.data
  }

  // Trading endpoints
  async getTrades(): Promise<Trade[]> {
    const response = await this.client.get('/api/trades/')
    return response.data
  }

  async getTradeOffers(): Promise<any[]> {
    const response = await this.client.get('/api/trades/offers')
    return response.data
  }

  async getAvailableForTrade(): Promise<Assignment[]> {
    const response = await this.client.get('/api/trades/assignments/available-for-trade')
    return response.data
  }

  async createTradeOffer(tradeData: {
    from_assignment_id: string
    to_assignment_id: string
    message?: string
  }): Promise<any> {
    const response = await this.client.post('/api/trades/create-offer', tradeData)
    return response.data
  }

  async acceptTradeOffer(offerId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/trades/${offerId}/accept`)
    return response.data
  }

  async rejectTradeOffer(offerId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/trades/${offerId}/reject`)
    return response.data
  }

  async createTrade(tradeData: {
    to_user_id: string
    from_assignment_id: string
    to_assignment_id?: string
    points_offered: number
    message?: string
  }): Promise<Trade> {
    const response = await this.client.post('/api/trades/', tradeData)
    return response.data
  }

  // Marketplace endpoints
  async getMarketplaceOffers(): Promise<MarketplaceOffer[]> {
    const response = await this.client.get('/api/marketplace/offers')
    return response.data?.offers || response.data || []
  }

  async getMyMarketplaceOffers(): Promise<MarketplaceOffer[]> {
    const response = await this.client.get('/api/marketplace/my-offers')
    return response.data?.offers || response.data || []
  }

  async getMarketplaceAssignments(): Promise<Assignment[]> {
    const response = await this.client.get('/api/marketplace/assignments')
    return response.data?.assignments || response.data || []
  }

  async createMarketplaceOffer(data: {
    assignment_id: string
    asking_points: number
  }): Promise<{ success: boolean; offer_id: string; message: string }> {
    const response = await this.client.post('/api/marketplace/create-offer', data)
    return response.data
  }

  async acceptMarketplaceOffer(offerId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/marketplace/${offerId}/accept`)
    return response.data
  }

  async cancelMarketplaceOffer(offerId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/marketplace/${offerId}/cancel`)
    return response.data
  }

  // Open Chore Bidding endpoints (reverse auction)
  async getOpenChoresForBidding(): Promise<OpenChoreWithBids[]> {
    const response = await this.client.get('/api/open-chores')
    return response.data?.open_chores || response.data || []
  }

  async getOpenChoreDetail(assignmentId: string): Promise<OpenChoreWithBids> {
    const response = await this.client.get(`/api/open-chores/${assignmentId}`)
    return response.data
  }

  async placeOpenChoreBid(assignmentId: string, bidPoints: number): Promise<{
    success: boolean
    bid_id: string
    bid_points: number
    message: string
    expires_at: string
  }> {
    const response = await this.client.post(`/api/open-chores/${assignmentId}/bid`, {
      bid_points: bidPoints
    })
    return response.data
  }

  async getMyOpenChoreBids(): Promise<MyOpenChoreBid[]> {
    const response = await this.client.get('/api/open-chores/my-bids')
    return response.data?.bids || response.data || []
  }

  async closeBidding(assignmentId: string): Promise<{
    message: string
    winner_id: string
    winner_name: string
    winning_bid: number
  }> {
    const response = await this.client.post(`/api/open-chores/${assignmentId}/close`)
    return response.data
  }

  // Achievement endpoints
  async getAchievements(): Promise<Achievement[]> {
    const response = await this.client.get('/api/achievements/')
    return response.data
  }

  async getMyAchievements(): Promise<UserAchievement[]> {
    const response = await this.client.get('/api/achievements/my-achievements')
    return response.data
  }

  // Reward endpoints
  async getRewards(): Promise<Reward[]> {
    const response = await this.client.get('/api/rewards/')
    return response.data
  }

  async purchaseReward(rewardId: string): Promise<RewardPurchase> {
    const response = await this.client.post(`/api/rewards/${rewardId}/purchase`)
    return response.data
  }

  async redeemReward(data: { reward_id: string; notes?: string }): Promise<any> {
    const response = await this.client.post(`/api/rewards/${data.reward_id}/redeem`, { notes: data.notes })
    return response.data
  }

  async getMyRedemptions(): Promise<any[]> {
    const response = await this.client.get('/api/rewards/my-redemptions')
    return response.data
  }

  // Notification endpoints
  async getNotifications(): Promise<Notification[]> {
    const response = await this.client.get('/api/notifications/')
    return response.data
  }

  async markNotificationRead(notificationId: string): Promise<void> {
    await this.client.post(`/api/notifications/${notificationId}/mark-read`)
  }

  // Schedule endpoints
  async getDailySchedule(date: string): Promise<any> {
    const response = await this.client.get(`/api/schedule/daily/${date}`)
    return response.data
  }

  async getScheduleRange(startDate: string, endDate: string): Promise<any> {
    // Add cache-busting parameter to ensure fresh data
    const timestamp = Date.now()
    const response = await this.client.get(`/api/schedule/range/${startDate}/${endDate}?t=${timestamp}`)
    return response.data
  }

  async getFamilySchedule(startDate: string, endDate: string): Promise<any> {
    // Add cache-busting parameter to ensure fresh data
    const timestamp = Date.now()
    const response = await this.client.get(`/api/schedule/range/${startDate}/${endDate}?t=${timestamp}`)
    return response.data.schedule || []
  }

  async getWeekendPattern(weeks: number = 8, startDate?: string): Promise<any> {
    const params = new URLSearchParams()
    params.append('weeks', weeks.toString())
    if (startDate) params.append('start_date', startDate)
    
    const response = await this.client.get(`/api/schedule/weekend-pattern?${params}`)
    return response.data
  }

  async checkChildPresence(username: string, date: string): Promise<any> {
    const response = await this.client.get(`/api/schedule/presence/${username}/${date}`)
    return response.data
  }

  // Calendar export endpoints
  async generateCalendarSubscription(): Promise<any> {
    const response = await this.client.get('/api/calendar/subscription/generate')
    return response.data
  }

  async revokeCalendarSubscription(): Promise<any> {
    const response = await this.client.delete('/api/calendar/subscription/revoke')
    return response.data
  }

  async getCalendarFormats(): Promise<any> {
    const response = await this.client.get('/api/calendar/formats')
    return response.data
  }

  async createScheduleException(exceptionData: {
    user_username: string
    exception_name: string
    start_date: string
    end_date: string
    exception_type: string
    notes?: string
  }): Promise<any> {
    const response = await this.client.post('/api/schedule/exceptions', exceptionData)
    return response.data
  }

  async getScheduleExceptions(username?: string, activeOnly: boolean = true): Promise<any> {
    const params = new URLSearchParams()
    if (username) params.append('username', username)
    if (activeOnly) params.append('active_only', 'true')
    
    const response = await this.client.get(`/api/schedule/exceptions?${params}`)
    return response.data
  }

  async deleteScheduleException(exceptionId: string): Promise<any> {
    const response = await this.client.delete(`/api/schedule/exceptions/${exceptionId}`)
    return response.data
  }

  async validateScheduleSetup(): Promise<any> {
    const response = await this.client.get('/api/schedule/validate')
    return response.data
  }

  async getScheduleSummary(days: number = 30): Promise<any> {
    const response = await this.client.get(`/api/schedule/summary?days=${days}`)
    return response.data
  }

  // Google Calendar Integration
  async getGoogleCalendars(): Promise<{
    calendars: Array<{
      id: string
      name: string
      description: string
      syncDirection: string
      syncTypes: string[]
      isPrivate: boolean
      owner: string
      lastSync?: string
    }>
    sync_rules: Array<{
      calendar_id: string
      event_types: string[]
      sync_direction: string
      privacy_level: string
      auto_sync: boolean
    }>
  }> {
    const response = await this.client.get('/api/calendar/google/calendars')
    return response.data
  }

  async createGoogleCalendar(calendarData: {
    name: string
    description: string
    privacy: string
    share_with: string[]
    sync_direction: string
    sync_types: string[]
  }): Promise<{
    calendar_id: string
    calendar_name: string
    share_url: string
  }> {
    const response = await this.client.post('/api/calendar/google/create', calendarData)
    return response.data
  }

  async connectGoogleCalendar(connectionData: {
    calendar_id: string
    sync_direction: string
    sync_types: string[]
    privacy_level: string
  }): Promise<{ message: string }> {
    const response = await this.client.post('/api/calendar/google/connect', connectionData)
    return response.data
  }

  async updateCalendarSyncRule(calendarId: string, ruleData: {
    event_types?: string[]
    sync_direction?: string
    privacy_level?: string
    auto_sync?: boolean
  }): Promise<{ message: string }> {
    const response = await this.client.put(`/api/calendar/google/sync-rules/${calendarId}`, ruleData)
    return response.data
  }

  async manualCalendarSync(calendarId: string, direction: 'import' | 'export' | 'both'): Promise<{
    events_processed: number
    direction: string
    calendar_name: string
  }> {
    const response = await this.client.post(`/api/calendar/google/sync/${calendarId}`, { direction })
    return response.data
  }

  async getGoogleCalendarEvents(calendarId: string, startDate?: string, endDate?: string): Promise<Array<{
    id: string
    title: string
    description: string
    start: string
    end: string
    allDay: boolean
    location: string
    attendees: string[]
    creator: string
    status: string
  }>> {
    const params = new URLSearchParams()
    params.append('calendar_id', calendarId)
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    
    const response = await this.client.get(`/api/calendar/google/events?${params}`)
    return response.data
  }


  // Parent Assignment Endpoints
  async getAllChores(category?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    const response = await this.client.get(`/api/chores?${params}`)
    return response.data
  }

  async getChildrenForAssignment(dateFilter?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (dateFilter) params.append('date_filter', dateFilter)
    const response = await this.client.get(`/api/parent/children?${params}`)
    return response.data
  }


  // Rotation Groups
  async getRotationGroups(activeOnly = true): Promise<any[]> {
    const response = await this.client.get(`/api/rotation-groups?active_only=${activeOnly}`)
    return response.data
  }

  async createRotationGroup(groupData: {
    name: string
    description?: string
    chore_ids: string[]
    frequency: string
    eligible_children: string[]
    distribution_strategy: string
    min_children_required: number
    max_children_per_assignment: number
    start_date: string
    reset_fairness_period: string
  }): Promise<any> {
    const response = await this.client.post('/api/rotation-groups/create', groupData)
    return response.data
  }

  async assignRotationGroup(data: {
    rotation_group_id: string
    date: string
    override_children?: string[]
  }): Promise<any> {
    const response = await this.client.post('/api/rotation-groups/assign', data)
    return response.data
  }

  async createRecurringAssignments(data: {
    rotation_group_id: string
    start_date: string
    end_condition: 'specific_date' | 'until_18'
    end_date?: string
    skip_existing?: boolean
  }): Promise<{
    message: string
    total_dates: number
    created: number
    skipped: number
    errors: string[] | null
    start_date: string
    end_date: string
    frequency: string
  }> {
    const response = await this.client.post('/api/rotation-groups/assign-recurring', data)
    return response.data
  }

  async getGroupAssignments(
    groupId: string, 
    startDate?: string, 
    endDate?: string
  ): Promise<any[]> {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    
    const response = await this.client.get(
      `/api/rotation-groups/${groupId}/assignments?${params}`
    )
    return response.data
  }

  async getRotationSchedule(startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    
    const url = `/api/rotation-groups/schedule${params.toString() ? '?' + params.toString() : ''}`
    const response = await this.client.get(url)
    return response.data
  }

  // Kiosk endpoints (user JWT or paired device token, added by the interceptor)
  async getKioskStatus(): Promise<any> {
    const response = await this.client.get('/api/kiosk/status')
    return response.data
  }

  async getKioskWeather(): Promise<any> {
    const response = await this.client.get('/api/kiosk/weather')
    return response.data
  }

  async getKioskHouseStatus(): Promise<any> {
    const response = await this.client.get('/api/kiosk/house-status')
    return response.data
  }

  // Netflix-style kiosk pairing (kiosk generates code, parent approves).
  // Same platform pairing API as the LiveView kiosk and TV app. Codes look like
  // K-ABC123; parents enter the six characters after the prefix and the
  // approve endpoint tries the K-/TV-/D- prefixes itself.
  async requestKioskPairing(): Promise<{
    code: string
    device_type: string
    expires_at: string
    expires_in: number
  }> {
    const response = await this.client.post('/api/platform/device/request-code', {
      device_type: 'kiosk',
      device_name: 'Kiosk Display',
    })
    return response.data
  }

  // The token is handed out once, on the first poll after approval; later
  // polls report "consumed".
  async checkKioskPairing(code: string): Promise<{
    status: 'pending' | 'approved' | 'expired' | 'consumed' | 'revoked'
    token?: string
    family_slug?: string
  }> {
    const response = await this.client.get(
      `/api/platform/device/check-code/${encodeURIComponent(code)}`
    )
    return response.data
  }

  async approveKioskPairing(data: { pairing_code: string }): Promise<{
    device_id: string
    device_name: string
    paired_at: string
    message: string
  }> {
    const response = await this.client.post('/api/parent/approve-pairing', data)
    return response.data
  }

  async generateRotationSchedule(groupId: string, daysAhead = 30): Promise<any> {
    const response = await this.client.post(
      `/api/rotation-groups/generate-schedule?group_id=${groupId}&days_ahead=${daysAhead}`
    )
    return response.data
  }

  async overrideFairnessTracking(
    groupId: string,
    childId: string,
    completionCount: number
  ): Promise<any> {
    const response = await this.client.put(
      `/api/rotation-groups/${groupId}/fairness-override?child_id=${childId}&completion_count=${completionCount}`
    )
    return response.data
  }

  async deleteRotationGroup(groupId: string): Promise<any> {
    const response = await this.client.delete(`/api/rotation-groups/${groupId}`)
    return response.data
  }

  async updateRotationGroup(groupId: string, updateData: {
    name?: string
    description?: string
    frequency?: string
    distribution_strategy?: string
    min_children_required?: number
    max_children_per_assignment?: number
    reset_fairness_period?: string
  }): Promise<any> {
    const response = await this.client.put(`/api/rotation-groups/${groupId}`, updateData)
    return response.data
  }

  async deleteRotationAssignment(groupId: string, date: string): Promise<any> {
    const response = await this.client.delete(`/api/rotation-groups/${groupId}/assignments/${date}`)
    return response.data
  }

  // Family Events and Kid Requests
  async createFamilyEvent(eventData: {
    event_name: string
    event_type: string
    start_date: string
    end_date: string
    start_time?: string
    end_time?: string
    description?: string
    location?: string
    requires_transport: boolean
    affects_chores: boolean
  }): Promise<any> {
    const response = await this.client.post('/api/events/family-events', eventData)
    return response.data
  }

  async createKidRequest(requestData: {
    request_type: string
    title: string
    description?: string
    urgency: string
  }): Promise<any> {
    const response = await this.client.post('/api/events/kid-requests', requestData)
    return response.data
  }


  async getFamilyEvents(status?: string, startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    
    const response = await this.client.get(`/api/events/family-events?${params}`)
    return response.data
  }

  async getKidRequests(status?: string, urgency?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    if (urgency) params.append('urgency', urgency)
    
    const response = await this.client.get(`/api/events/kid-requests?${params}`)
    return response.data
  }

  async approveEvent(eventId: string): Promise<any> {
    const response = await this.client.patch(`/api/events/family-events/${eventId}/approve`)
    return response.data
  }

  async updateKidRequest(requestId: string, status: string, notes?: string): Promise<any> {
    const response = await this.client.patch(`/api/events/kid-requests/${requestId}?status=${status}`)
    return response.data
  }

  async deleteEvent(eventId: string): Promise<any> {
    const response = await this.client.delete(`/api/events/family-event/${eventId}`)
    return response.data
  }

  // Availability endpoints
  async getAvailabilityOverrides(userId: string, startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    
    const response = await this.client.get(`/api/availability/overrides/${userId}?${params}`)
    return response.data
  }

  async getCoverageRequests(): Promise<any[]> {
    const response = await this.client.get('/api/availability/coverage-requests')
    return response.data
  }

  async claimCoverageRequest(requestId: string): Promise<any> {
    const response = await this.client.post(`/api/availability/coverage-request/${requestId}/claim`)
    return response.data
  }

  async createAvailabilityOverride(overrideData: {
    override_date: string
    override_type: string
    reason?: string
    location?: string
    all_day: boolean
    start_time?: string
    end_time?: string
  }): Promise<any> {
    const response = await this.client.post('/api/availability/overrides', overrideData)
    return response.data
  }

  async getAvailabilityApprovals(): Promise<any[]> {
    try {
      const response = await this.client.get('/api/availability/pending-approvals')
      return response.data
    } catch (error: any) {
      // Return empty array for 404 - endpoint not implemented yet
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  }

  async approveAvailabilityOverride(overrideId: string, coverage_status?: string): Promise<any> {
    const response = await this.client.put(`/api/availability/approve/${overrideId}`, {
      coverage_status
    })
    return response.data
  }

  async rejectAvailabilityOverride(overrideId: string, rejection_reason?: string): Promise<any> {
    const response = await this.client.put(`/api/availability/reject/${overrideId}`, {
      rejection_reason
    })
    return response.data
  }

  // Saved Locations
  async getSavedLocations(): Promise<{ id: string; name: string; type: string; address?: string }[]> {
    try {
      const response = await this.client.get('/api/availability/locations')
      return response.data || []
    } catch (error: any) {
      if (error.response?.status === 404) {
        return []
      }
      throw error
    }
  }

  async saveLocation(data: {
    name: string
    type: string
    street_address?: string
    city?: string
    state?: string
    zip_code?: string
  }): Promise<{ id: string; name: string; type: string; address?: string }> {
    const response = await this.client.post('/api/availability/locations', data)
    return response.data
  }

  async updateLocation(id: string, data: {
    name: string
    type: string
    street_address?: string
    city?: string
    state?: string
    zip_code?: string
  }): Promise<{ id: string; name: string; type: string; address?: string }> {
    const response = await this.client.put(`/api/availability/locations/${id}`, data)
    return response.data
  }

  async deleteLocation(id: string): Promise<void> {
    await this.client.delete(`/api/availability/locations/${id}`)
  }

  // Meal option management
  async getMealOptions(): Promise<any[]> {
    const response = await this.client.get('/api/meals/options')
    return response.data || []
  }

  async createMealOption(mealData: any): Promise<any> {
    const response = await this.client.post('/api/meals/options', mealData)
    return response.data
  }

  async updateMealOption(mealId: string, mealData: any): Promise<any> {
    const response = await this.client.put(`/api/meals/options/${mealId}`, mealData)
    return response.data
  }

  async deleteMealOption(mealId: string): Promise<any> {
    const response = await this.client.delete(`/api/meals/options/${mealId}`)
    return response.data
  }

  // Shopping list
  async getShoppingList(): Promise<any[]> {
    const response = await this.client.get('/api/shopping/lists')
    return response.data
  }

  async saveShoppingList(items: any[]): Promise<any> {
    const response = await this.client.post('/api/meals/shopping-list', items)
    return response.data
  }

  async addShoppingItem(name: string, quantity?: string, listId?: string): Promise<any> {
    const response = await this.client.post('/api/shopping/add-custom-item', { name, quantity, list_id: listId })
    return response.data
  }

  async generateShoppingListFromMeals(): Promise<any> {
    const response = await this.client.get('/api/shopping/generate')
    return response.data
  }

  // Health check
  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await this.client.get('/health')
    return response.data
  }

  // Recipe Management
  async getRecipes(params?: {
    meal_type?: string
    tag?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<any[]> {
    const searchParams = new URLSearchParams()
    if (params?.meal_type) searchParams.append('meal_type', params.meal_type)
    if (params?.tag) searchParams.append('tag', params.tag)
    if (params?.search) searchParams.append('search', params.search)
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())
    
    const response = await this.client.get(`/api/recipes?${searchParams}`)
    return response.data
  }

  async getRecipe(recipeId: string): Promise<any> {
    const response = await this.client.get(`/api/recipes/${recipeId}`)
    return response.data
  }

  async createRecipe(recipe: any): Promise<any> {
    const response = await this.client.post('/api/recipes', recipe)
    return response.data
  }

  async updateRecipe(recipeId: string, updates: any): Promise<any> {
    const response = await this.client.put(`/api/recipes/${recipeId}`, updates)
    return response.data
  }

  async deleteRecipe(recipeId: string): Promise<any> {
    const response = await this.client.delete(`/api/recipes/${recipeId}`)
    return response.data
  }

  async addRecipeIngredient(recipeId: string, ingredient: any): Promise<any> {
    const response = await this.client.post(`/api/recipes/${recipeId}/ingredients`, ingredient)
    return response.data
  }

  async addRecipeInstruction(recipeId: string, instruction: any): Promise<any> {
    const response = await this.client.post(`/api/recipes/${recipeId}/instructions`, instruction)
    return response.data
  }

  async toggleRecipeFavorite(recipeId: string): Promise<any> {
    const response = await this.client.post(`/api/recipes/${recipeId}/favorite`)
    return response.data
  }

  async addRecipeReview(recipeId: string, review: {
    rating: number
    comment?: string
    would_make_again?: boolean
  }): Promise<any> {
    const response = await this.client.post(`/api/recipes/${recipeId}/review`, review)
    return response.data
  }

  async getRecipeTags(): Promise<any[]> {
    const response = await this.client.get('/api/recipes/tags/all')
    return response.data
  }

  // Subscription endpoints
  async getSubscriptionStatus(): Promise<any> {
    const response = await this.client.get('/api/subscription/status')
    return response.data
  }

  async createCheckoutSession(data: {
    price_id: string
    success_url: string
    cancel_url: string
  }): Promise<{ checkout_url: string }> {
    const response = await this.client.post('/api/subscriptions/checkout', data)
    return response.data
  }

  async getCustomerPortal(returnUrl: string): Promise<{ portal_url: string }> {
    const response = await this.client.post('/api/subscriptions/portal', {
      return_url: returnUrl
    })
    return response.data
  }

  // Family-to-family referrals
  async getReferralInfo(): Promise<{
    code: string
    share_url: string
    referrals_count: number
    months_earned: number
    cap: number
    remaining: number
  }> {
    const response = await this.client.get('/api/referral/info')
    return response.data
  }

  // Session management endpoints
  async getSessions(): Promise<{ sessions: SessionInfo[] }> {
    const response = await this.client.get('/api/sessions')
    return response.data
  }

  async getMySessions(): Promise<{ sessions: SessionInfo[] }> {
    const response = await this.client.get('/api/sessions/me')
    return response.data
  }

  async revokeSession(sessionId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/sessions/${sessionId}/revoke`)
    return response.data
  }

  async revokeAllOtherSessions(): Promise<{ message: string }> {
    const response = await this.client.post('/api/sessions/revoke-all')
    return response.data
  }

  async revokeUserSessions(userId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/sessions/user/${userId}/revoke-all`)
    return response.data
  }

  // Setup wizard endpoints
  async getSetupStatus(): Promise<SetupStatus> {
    const response = await this.client.get('/api/setup/status')
    return response.data
  }

  async completeSetup(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/api/setup/complete')
    return response.data
  }

  async inviteCoparent(email: string, displayName: string, role?: string): Promise<{ success: boolean; message: string; warning?: string }> {
    const response = await this.client.post('/api/setup/invite-coparent', {
      email,
      display_name: displayName,
      role: role || 'parent'
    })
    return response.data
  }

  async setChoreSchedule(choice: 'daily' | 'weekly' | 'skip'): Promise<{ message: string; frequency: string | null }> {
    const body = choice === 'skip' ? { skip: true } : { frequency: choice }
    const response = await this.client.post('/api/setup/chore-schedule', body)
    return response.data
  }
}

export interface SetupStatus {
  setup_completed: boolean
  setup_completed_at?: string
  is_first_login: boolean
  steps: {
    has_kids: boolean
    has_chores: boolean
    has_rewards: boolean
    has_coparent: boolean
    coparent_invited: boolean
    chore_schedule_set?: boolean
  }
}

export interface SessionInfo {
  id: string
  user_id: string
  username?: string
  ip_address?: string
  user_agent?: string
  device_type: string
  created_at: string
  last_active_at: string
  expires_at: string
  is_current: boolean
}

export const apiClient = new ApiClient()
export default apiClient